import React, { ReactNode } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { explodePresetContentFilters } from 'lane-shared/helpers/content';
import useSectionSearchOptionsMenu from 'lane-shared/hooks/useSectionSearchOptionsMenu';
import { MetatagFilterUI } from 'lane-shared/types/filters/MetatagFilter';
import { PresetContentFilter } from 'constants-content';
import { SearchOptions } from 'lane-shared/types/filters/SearchOptions';

import { H5 } from 'components/typography';
import { Input } from 'components';

import DateRangePicker from '../form/DatePickers/DateRangePicker';
import TimeRangePicker from '../form/DatePickers/TimeRangePicker';
import DistanceSlider from '../form/DistanceSlider';
import PriceSlider from '../form/PriceSlider';
import Toggle from '../form/Toggle';
import MetatagFilterMenu from './MetatagFilterMenu';

import styles from './SectionSearchOptionsMenu.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  metatags?: MetatagFilterUI[];
  filters: PresetContentFilter[] | null | undefined;
  timeZone?: string;
  searchOptions: SearchOptions | null | undefined;
  allowDisableFilter?: boolean;
  onSearchOptionsUpdated: (update: Partial<SearchOptions>) => void;
};

export default function SectionSearchOptionsMenu({
  className,
  style,
  metatags,
  filters,
  timeZone,
  searchOptions,
  allowDisableFilter = false,
  onSearchOptionsUpdated,
}: Props) {
  const { t } = useTranslation();
  const presetFilters: ReactNode[] = [];
  const presetSorts: any = [];

  const {
    filterTimeRange,
    filterLocation,
    filterQuantity,
    filterPrice,
    filterAvailable,
  } = explodePresetContentFilters(searchOptions?.filters);

  const {
    timeRangeChangeHandler,
    availableNowChangeHandler,
    anyTimeChangeHandler,
    distanceChangeHandler,
    areTimeOptionsEnabled,
    isAnyTimeSelected,
    isAvailableNowSelected,
    isTimeRangeDisabled,
  } = useSectionSearchOptionsMenu({
    searchOptions: searchOptions ?? undefined,
    onSearchOptionsUpdated,
  });

  function validateNumberInput(
    val: string,
    prevVal: number,
    minBound: number,
    maxBound?: number
  ): number {
    if (String(val) === '') {
      return 0;
    }

    const parsed = Number(val);

    if (Number.isNaN(parsed)) {
      return prevVal;
    }

    if (parsed < minBound) {
      return minBound;
    }

    if (maxBound && parsed > maxBound) {
      return maxBound;
    }

    return parsed;
  }

  if (filterLocation) {
    presetFilters.push(
      <DistanceSlider
        key={PresetContentFilter.Location}
        className={styles.locationFilter}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'number | null | undefined' is not assignable... Remove this comment to see the full error message
        value={filterLocation.distance}
        min={0}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'number | null | undefined' is not assignable... Remove this comment to see the full error message
        max={filterLocation.maxDistance}
        onChange={distanceChangeHandler}
      />
    );
  }

  if (filterPrice) {
    presetFilters.push(
      <PriceSlider
        label="Price Range"
        key={PresetContentFilter.FeaturePaymentPrice}
        min={filterPrice.lowestPrice}
        max={filterPrice.highPrice}
        values={{ min: filterPrice.minPrice, max: filterPrice.maxPrice }}
        onChange={({ min, max }) => {
          if (min !== undefined) {
            filterPrice.minPrice = min;
          }

          if (max !== undefined) {
            filterPrice.maxPrice = max;
          }

          onSearchOptionsUpdated({
            filters: [...searchOptions!.filters],
          });
        }}
      />
    );
  }

  if (filterQuantity) {
    presetFilters.push(
      <div
        className={styles.sliderControl}
        key={PresetContentFilter.FeatureQuantityRemaining}
      >
        <H5>{t('Quantity available')}</H5>
        <div className={styles.quantityInputsRow}>
          <div>
            <H5>
              {t(
                'web.admin.channel.content.components.sectionSearchOptionsMenu.filters.Min'
              )}
            </H5>
            <Input
              type="text"
              value={(filterQuantity.quantity ?? 1).toString()}
              onChange={val => {
                const currentMin = filterQuantity.quantity ?? 1;
                const next = validateNumberInput(
                  val,
                  currentMin,
                  1,
                  filterQuantity?.maxQuantity
                );

                filterQuantity.quantity = next;
                onSearchOptionsUpdated({
                  areFiltersApplied: true,
                  filters: [...searchOptions!.filters],
                });
              }}
            />
          </div>
          <div>
            <H5>
              {t(
                'web.admin.channel.content.components.sectionSearchOptionsMenu.filters.Max'
              )}
            </H5>
            <Input
              type="text"
              value={(
                (typeof filterQuantity.maxQuantity === 'number'
                  ? filterQuantity.maxQuantity
                  : filterQuantity.quantity) ?? 1
              ).toString()}
              onChange={val => {
                const currentMax =
                  (typeof filterQuantity.maxQuantity === 'number'
                    ? filterQuantity.maxQuantity
                    : filterQuantity.quantity) ?? 1;
                const minBound = filterQuantity.quantity ?? 1;
                const next = validateNumberInput(
                  val,
                  currentMax,
                  minBound,
                  Number.MAX_SAFE_INTEGER
                );

                filterQuantity.maxQuantity = next;
                onSearchOptionsUpdated({
                  areFiltersApplied: true,
                  filters: [...searchOptions!.filters],
                });
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (filterAvailable) {
    presetFilters.push(
      <div key="Available now" className={styles.toggleRow}>
        <Toggle
          doTranslate
          value={isAvailableNowSelected}
          text="Show only available now"
          onChange={availableNowChangeHandler}
        />
      </div>
    );
  }

  if (areTimeOptionsEnabled) {
    presetFilters.push(
      <div key="Available anytime" className={styles.toggleRow}>
        <Toggle
          doTranslate
          value={isAnyTimeSelected}
          text="Available anytime"
          onChange={anyTimeChangeHandler}
        />
      </div>
    );
  }

  if (filterTimeRange) {
    if (
      filters?.includes(PresetContentFilter.FeatureReservableAvailableMinutes)
    ) {
      presetFilters.push(
        <div
          className={styles.timeControl}
          key={PresetContentFilter.FeatureReservableAvailableMinutes}
        >
          <TimeRangePicker
            disabled={isTimeRangeDisabled}
            value={filterTimeRange}
            slotSize={30}
            timeZone={timeZone}
            // @ts-expect-error ts-migrate(2322) FIXME: Type '(value: { startDate: Date; endDate: Date; })... Remove this comment to see the full error message
            onChange={timeRangeChangeHandler}
            showTimeSlider={false}
          />
        </div>
      );
    }

    if (filters?.includes(PresetContentFilter.FeatureReservableAvailableDays)) {
      presetFilters.push(
        <div
          className={styles.timeControl}
          key={PresetContentFilter.FeatureReservableAvailableDays}
        >
          <DateRangePicker
            disabled={isTimeRangeDisabled}
            timeZone={timeZone}
            startDate={filterTimeRange?.startDate}
            endDate={filterTimeRange?.endDate}
            minRangeSize={1}
            maxRangeSize={30}
            // @ts-expect-error ts-migrate(2322) FIXME: Type '(value: { startDate: Date; endDate: Date; })... Remove this comment to see the full error message
            onChange={timeRangeChangeHandler}
          />
        </div>
      );
    }
  }

  if (!searchOptions) {
    return null;
  }

  return (
    <div
      className={cx(styles.SectionSearchOptionsMenu, className)}
      style={style}
    >
      {presetFilters}
      {presetSorts}

      <MetatagFilterMenu
        filters={searchOptions.metatagFilters}
        metatags={metatags}
        allowDisableFilter={allowDisableFilter}
        onFiltersUpdated={metatagFilters => {
          onSearchOptionsUpdated({ metatagFilters });
        }}
      />
    </div>
  );
}
